import { z } from 'zod'
import { CaptureState } from '../enums'
import { Id, Timestamp } from './common'
import { ALLOWED_AUDIO_TYPES, MAX_VOICE_NOTE_SECONDS } from './uploads'

export const TranscriptState = z.enum(['none', 'pending', 'done', 'unavailable'])
export type TranscriptState = z.infer<typeof TranscriptState>

/**
 * A note made on the trail. Written to the device first and synced later,
 * so `client_id` (made on the device) is what makes a replay safe.
 * Location is private to the owner and never leaves in exports.
 */
export const Capture = z.object({
  id: Id,
  client_id: Id,
  bike_id: Id.nullable(),
  component_id: Id.nullable(),
  ride_id: Id.nullable(),
  text: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  accuracy_m: z.number().nullable(),
  captured_at: Timestamp,
  state: CaptureState,
  job_id: Id.nullable(),
  /** Signed link to the voice note, if there is one. */
  audio_url: z.string().nullable(),
  audio_duration_s: z.number().nullable(),
  transcript: z.string().nullable(),
  transcript_state: TranscriptState,
  created_at: Timestamp,
})
export type Capture = z.infer<typeof Capture>

export const CaptureList = z.object({ captures: z.array(Capture) })
export type CaptureList = z.infer<typeof CaptureList>

export const CreateCaptureBody = z
  .object({
    client_id: Id,
    bike_id: Id.nullable().optional(),
    component_id: Id.nullable().optional(),
    text: z.string().trim().max(4000).default(''),
    lat: z.number().min(-90).max(90).nullable().optional(),
    lng: z.number().min(-180).max(180).nullable().optional(),
    accuracy_m: z.number().min(0).max(100_000).nullable().optional(),
    captured_at: Timestamp,
    /** A voice note follows, so the text may be empty. */
    has_audio: z.boolean().default(false),
    /** Made in workshop mode: file it straight onto this job instead of the inbox. */
    job_id: Id.nullable().optional(),
    /** Made from the post-ride prompt: the ride it is about. Sets the bike if none is given. */
    ride_id: Id.nullable().optional(),
  })
  .refine((b) => b.text.length > 0 || b.has_audio, {
    message: 'Write something about what you noticed.',
    path: ['text'],
  })
  .refine((b) => (b.lat == null) === (b.lng == null), {
    message: 'Send both latitude and longitude, or neither.',
    path: ['lat'],
  })
export type CreateCaptureBody = z.input<typeof CreateCaptureBody>

export const CaptureListQuery = z.object({
  state: CaptureState.optional(),
  bike_id: Id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
})

/** Triage and tidy-up. Converting and merging go through the jobs endpoints. */
export const UpdateCaptureBody = z
  .object({
    state: z.enum(['inbox', 'dismissed']),
    text: z.string().trim().min(1).max(4000),
    bike_id: Id.nullable(),
    component_id: Id.nullable(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateCaptureBody = z.input<typeof UpdateCaptureBody>

/** Attach an uploaded voice note to a capture. The object must already be in storage. */
export const AttachAudioBody = z.object({
  object_key: z.string().min(1).max(300),
  content_type: z.enum(ALLOWED_AUDIO_TYPES),
  duration_s: z.number().min(0).max(MAX_VOICE_NOTE_SECONDS),
})
export type AttachAudioBody = z.infer<typeof AttachAudioBody>
